import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useToast } from "../../components/common/ToastContext.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";

const ForgotPassword = () => {
  const { showToast } = useToast();
  const { themeName } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Check form validity whenever email changes
  useEffect(() => {
    const isValid = email.trim() !== "";
    setIsFormValid(isValid);
  }, [email]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast("Please enter your email address", "warning");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("Please enter a valid email address", "warning");
      return;
    }

    try {
      setLoading(true);
      showToast("Sending reset link...", "info");

      // TODO: Implement actual password reset API call
      // For now, just simulate success
      setTimeout(() => {
        showToast("If an account with that email exists, a reset link has been sent.", "success");
        setEmail("");
        setLoading(false);
      }, 2000);
    } catch (error) {
      showToast("Failed to send reset link. Please try again.", "error");
      setLoading(false);
    }
  };

  // Determine text color based on theme
  const textColor = ["light", "brand", "pastel"].includes(themeName) ? "text-gray-800" : "text-white";

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        themeName === "light"
          ? "bg-gradient-to-r from-primary to-success"
          : themeName === "dark"
          ? "bg-gradient-to-r from-primary to-background"
          : "bg-primary"
      }`}
    >
      <div className="w-full max-w-md">
        <div
          className={`rounded-xl shadow-2xl overflow-hidden ${
            themeName === "dark" ? "bg-background" : "bg-white"
          }`}
        >
          {/* Card Header with Logo */}
          <div className="p-8 pb-0 text-center">
            <div className="w-24 h-24 mx-auto mb-4">
              <img
                src="/logo.png"
                alt="SocialsCollab Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h2 className={`text-2xl font-bold ${textColor} mb-2`}>Forgot Password</h2>
            <p className={`text-sm ${textColor} opacity-70 mb-6`}>
              Enter your registered email to receive a reset link
            </p>
          </div>

          {/* Forgot Password Form */}
          <div className="p-8 pt-0">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className={`w-full py-2.5 px-4 rounded-lg shadow-md text-white font-medium
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300
                  flex items-center justify-center
                  ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : isFormValid
                      ? "bg-success hover:bg-success/90 transform hover:scale-[1.02]"
                      : "bg-primary hover:bg-primary/90 transform hover:scale-[1.02]"
                  }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>

            {/* Back to Login Link */}
            <p className="mt-8 text-center text-sm">
              <span className={textColor}>Remember your password?</span>
              <Link
                to="/login"
                className={`ml-2 font-medium transition-all duration-200 hover:underline ${
                  ["light", "brand", "pastel"].includes(themeName)
                    ? "text-primary hover:text-primary/80"
                    : "text-white hover:text-gray-300"
                }`}
              >
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;